import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { VENTURES, VENTURE_DETAILS } from "../data/ventures";
import { FadeUp, SectionLabel, MaskedLine } from "../components/veerox/Reveal";
import { Footer } from "../components/veerox/Footer";

export default function VenturePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const venture = VENTURES.find((v) => v.id === id);
  const details = VENTURE_DETAILS[id];

  if (!venture || !details) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6">
        <h1 className="font-display text-3xl font-bold text-white">Venture not found</h1>
        <Link to="/" className="btn-primary rounded-full px-8 py-3 font-display font-semibold text-white">
          Back to Veerox Group
        </Link>
      </main>
    );
  }

  return (
    <>
      <main className="relative z-10" data-testid="venture-page">
        <section className="relative overflow-hidden pb-20 pt-32 md:pt-40">
          <div className="absolute inset-0 grid-bg" />
          <div
            className="pointer-events-none absolute -right-32 top-10 h-[420px] w-[420px] rounded-full blur-[150px]"
            style={{ background: `${venture.accent}1f` }}
          />
          <div className="relative mx-auto max-w-[1440px] px-6 md:px-12">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <Link
                to="/"
                data-testid="venture-page-back"
                className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.24em] text-slate-400 transition-colors duration-200 hover:text-cyan-300"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Veerox Group
              </Link>
            </motion.div>

            <div className="mt-10 flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-3xl">
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-center gap-5"
                >
                  <img
                    src={venture.logo}
                    alt={venture.name}
                    className="h-24 w-24 rounded-2xl object-cover ring-2"
                    style={{ boxShadow: `0 0 40px ${venture.accent}44`, borderColor: venture.accent }}
                  />
                  <div>
                    <span
                      className="font-mono-tech text-xs uppercase tracking-[0.28em]"
                      style={{ color: venture.accent }}
                    >
                      {venture.category} · Venture {venture.index}
                    </span>
                    <h1
                      data-testid="venture-page-title"
                      className="mt-2 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl"
                    >
                      <MaskedLine delay={0.25}>{venture.name}</MaskedLine>
                    </h1>
                  </div>
                </motion.div>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="mt-8 font-display text-xl font-semibold text-slate-200 md:text-2xl"
                >
                  {details.heroLine}
                </motion.p>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="mt-4 max-w-2xl text-base leading-relaxed text-slate-400"
                >
                  {venture.description}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.65 }}
                  className="mt-9 flex flex-wrap gap-4"
                >
                  <a
                    href={venture.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="venture-page-visit"
                    className="btn-primary inline-flex items-center gap-2 rounded-full px-8 py-4 font-display text-base font-semibold text-white"
                  >
                    Visit {venture.name}
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <button
                    onClick={() => navigate("/", { state: { scrollTo: "#contact" } })}
                    data-testid="venture-page-contact"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-8 py-4 font-display text-base font-semibold text-white backdrop-blur-md transition-colors duration-300 hover:bg-white/[0.08]"
                  >
                    Work With Us
                  </button>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.75 }}
                className="grid w-full max-w-sm grid-cols-1 gap-4"
              >
                {details.stats.map((s) => (
                  <div
                    key={s.label}
                    className="rounded-2xl border border-white/10 bg-slate-950/60 px-6 py-5 backdrop-blur-xl"
                  >
                    <span className="font-display text-3xl font-extrabold text-white">{s.value}</span>
                    <p className="mt-1 font-mono-tech text-[0.62rem] uppercase tracking-[0.2em] text-slate-400">
                      {s.label}
                    </p>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        <section className="relative py-16 md:py-24" data-testid="venture-story">
          <div className="mx-auto max-w-[1440px] px-6 md:px-12">
            <FadeUp>
              <SectionLabel>The Story</SectionLabel>
            </FadeUp>
            <div className="mt-10 grid grid-cols-1 gap-10 border-t border-white/10 pt-10 lg:grid-cols-2">
              {details.story.map((p, i) => (
                <FadeUp key={i} delay={i * 0.15}>
                  <div className="flex gap-6">
                    <span className="font-mono-tech text-4xl font-semibold text-slate-700">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-base leading-relaxed text-slate-300 md:text-lg">{p}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-16 md:py-24" data-testid="venture-services">
          <div
            className="pointer-events-none absolute left-0 top-20 h-80 w-80 rounded-full blur-[140px]"
            style={{ background: `${venture.accent}14` }}
          />
          <div className="mx-auto max-w-[1440px] px-6 md:px-12">
            <FadeUp>
              <SectionLabel>What We Do</SectionLabel>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                Services & <span className="text-gradient-cyan">capabilities.</span>
              </h2>
            </FadeUp>
            <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {details.services.map((s, i) => (
                <FadeUp key={s.title} delay={(i % 3) * 0.1}>
                  <div className="group h-full rounded-2xl border border-white/10 bg-slate-950/60 p-7 backdrop-blur-xl transition-[border-color,box-shadow] duration-300 hover:border-cyan-400/40 hover:shadow-[0_0_26px_rgba(0,229,255,0.1)]">
                    <span
                      className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10"
                      style={{ background: `${venture.accent}14` }}
                    >
                      <Check className="h-5 w-5" style={{ color: venture.accent }} />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-bold text-white">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-16 md:py-24" data-testid="venture-gallery">
          <div className="mx-auto max-w-[1440px] px-6 md:px-12">
            <FadeUp>
              <SectionLabel>Gallery</SectionLabel>
            </FadeUp>
            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
              {details.gallery.map((img, i) => (
                <FadeUp key={i} delay={i * 0.12} className={i === 0 ? "md:col-span-2" : ""}>
                  <div className="group relative h-72 overflow-hidden rounded-2xl border border-white/10 md:h-96">
                    <img
                      src={img}
                      alt={`${venture.name} showcase ${i + 1}`}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/70 via-transparent to-transparent" />
                  </div>
                </FadeUp>
              ))}
              <FadeUp delay={0.24}>
                <div
                  className="flex h-72 flex-col items-center justify-center rounded-2xl border border-white/10 bg-slate-950/60 backdrop-blur-xl md:h-96"
                  style={{ boxShadow: `inset 0 0 60px ${venture.accent}0d` }}
                >
                  <img
                    src={venture.logo}
                    alt={venture.name}
                    className="h-28 w-28 rounded-2xl object-cover ring-1 ring-white/15"
                    style={{ boxShadow: `0 0 40px ${venture.accent}44` }}
                  />
                  <p className="mt-5 font-display text-lg font-bold text-white">{venture.name}</p>
                  <p className="mt-1 font-mono-tech text-[0.6rem] uppercase tracking-[0.22em] text-slate-500">
                    {venture.tagline}
                  </p>
                </div>
              </FadeUp>
            </div>
          </div>
        </section>

        <section className="relative py-16 md:py-24">
          <div className="mx-auto max-w-[1440px] px-6 md:px-12">
            <FadeUp>
              <div className="glass relative overflow-hidden rounded-3xl p-10 text-center md:p-16">
                <div
                  className="pointer-events-none absolute -top-24 left-1/2 h-64 w-[480px] -translate-x-1/2 rounded-full blur-[120px]"
                  style={{ background: `${venture.accent}22` }}
                />
                <h2 className="relative font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                  Want to build with <span className="text-gradient-cyan">{venture.name}</span>?
                </h2>
                <p className="relative mx-auto mt-4 max-w-xl text-base text-slate-400">
                  Reach the Veerox team and let&apos;s make it happen.
                </p>
                <div className="relative mt-8 flex flex-wrap justify-center gap-4">
                  <button
                    onClick={() => navigate("/", { state: { scrollTo: "#contact" } })}
                    className="btn-primary inline-flex items-center gap-2 rounded-full px-8 py-4 font-display text-base font-semibold text-white"
                    data-testid="venture-cta-contact"
                  >
                    Get In Touch
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <a
                    href={venture.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-8 py-4 font-display text-base font-semibold text-white transition-colors duration-300 hover:bg-white/[0.08]"
                  >
                    Visit Website
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </FadeUp>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
